import React, { useState, useEffect, useMemo, useCallback } from "react";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import DataGrid, {
  Column,
  Format,
  SearchPanel,
  Toolbar,
  Item,
  Lookup,
  Button as DataButton,
  Editing,
  Form,
} from "devextreme-react/data-grid";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import FileUploader from "devextreme-react/file-uploader";
import LoadingSpinnerModal from "../../Components/LoadingSpinnerModal";
import { apiBaseURL } from "../../Utils/ProjectConstants";
import useFetch from "../../Hooks/useFetch";
import handlePostAddMiscCosts from "../../Utils/handlePOST/handlePostAddMiscCosts";
import ActionNotifications from "../../Components/ActionNotifications";
import ErrorMessage from "../../Components/ErrorMessage";
import "./styles.css";

const typeValues = [
  { value: 0, text: "Credit Card" },
  { value: 1, text: "Purchase Order" },
  { value: 2, text: "Travel Expense" },
  //id 3??
  { value: 4, text: "Invoices and Payables" },
];

export default function MiscCostsPage() {
  const navigate = useNavigate(),
    fetch = useFetch(navigate),
    [notifyResponse, setResponse] = useState({}),
    { data, mutate, error } = useSWR(`${apiBaseURL}/api/projects/misccost`),
    { data: billingData, error: billingError } = useSWR(
      `${apiBaseURL}/api/Project/BillingCodes`
    );
  const [billingCodes, setBillingCodes] = useState([]),
    [input, setInput] = useState([]),
    [formData, setFormData] = useState({}),
    [fileData, setFileData] = useState(new FormData()),
    [selectedRows, setSelectedRows] = useState([]),
    [popupVisible, setPopupVisible] = useState(false);

  useEffect(() => {
    const billingCodes = billingData?.data.map((x) => ({ text: x, value: x }));
    setBillingCodes(billingCodes);
  }, [billingData]);

  const fetchSuccessful = (response) => {
    setResponse(response);
    return (
      response?.success &&
      response?.data?.statusCode &&
      response?.success !== "Invalid Model"
    );
  };

  const onRowPrepared = (e) => {
    if (e.rowType === "header") {
      e.rowElement.style.backgroundColor = "#FFCD00";
      e.rowElement.style.fontWeight = "bold";
    }
  };

  const onRowRemoved = async (e) => {
    const id = e.data.id;
    const response = await fetch.delete(
      `${apiBaseURL}/api/projects/misccost?id=${id}`
    );
    fetchSuccessful(response);
  };

  const onEditingStart = (e) => {
    setFormData({
      id: e.data.id,
      billingCode: e.data.billingCode,
      type: e.data.type,
      AMT: e.data.total,
      vendorName: e.data.vendorName,
      date: e.data.date,
      description: e.data.description,
    });
  };

  const onSaving = async (e) => {
    if (e.changes?.length > 0) {
      //setShowSpinner(true);
      const changes = e.changes[0].data;

      const body = {
        ...formData,
        ...changes,
      };

      let response = await fetch.put(
        `${apiBaseURL}/api/projects/misccost`,
        body
      );

      if (fetchSuccessful(response)) {
        mutate();
      }
      //setShowSpinner(false);
    }
  };

  const handleDataLogic = useCallback(
    (response, isDataGood) => {
      setResponse(response);
      if (isDataGood) {
        mutate();
      }
      setFileData(new FormData());
    },
    [mutate, setResponse]
  );

  const handleFileUpload = async (e) => {
    const file = e.value[0]; // Get the uploaded file
    fileData.append("file", file); // Add the file to FormData
    setFileData(fileData);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" }); // Read the file
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const parsedData = XLSX.utils.sheet_to_json(ws, { header: 1 }); // Convert to JSON

      const convertExcelDate = (excelDate) => {
        const date = new Date(1900, 0, excelDate - 1); // Excel date starts from Jan 1, 1900
        return date.toISOString().split("T")[0]; // Format to 'YYYY-MM-DD' or adjust as needed
      };

      // Format the data for the grid (assuming first row is the header)
      const formattedData = parsedData.slice(1).map((row, index) => ({
        id: index,
        billingCode: row[0] || "",
        type: row[1] || "",
        total: row[2] || "",
        vendorName: row[3] || "",
        date: typeof row[4] === "number" ? convertExcelDate(row[4]) : row[4], // Convert if it's a number
        description: row[5] || "",
      }));

      setInput(formattedData);
      setPopupVisible(true);
    };
    reader.readAsBinaryString(file);
  };

  const handleSubmit = useCallback(
    async (e) => {
      //if (selectedRows.length === 0) {
      //    alert('Please select at least one row.');
      //    return;
      //}
      //const selectedData = data.filter((row) => selectedRows.includes(row.id));

      try {
        // Send selected data to Postman URL
        //const response = await fetch.postFile(`${apiBaseURL}/api/projects/misccost`, selectedData);
        const { response, isDataGood } = await handlePostAddMiscCosts(
          fileData,
          fetch
        );
        handleDataLogic(response, isDataGood);
      } catch (error) {
        console.error("Error uploading data:", error);
      }
      setSelectedRows([]);
      setPopupVisible(false);
    },
    [selectedRows]
  );

  const cancelOptions = useMemo(() => {
    return {
      stylingMode: "outlined",
      text: "cancel",
      onClick: () => setPopupVisible(false),
    };
  }, []);
  const submitOptions = useMemo(() => {
    return {
      stylingMode: "contained",
      text: "submit",
      onClick: () => handleSubmit(),
    };
  }, [handleSubmit]);

  const renderGrid = () => {
    return (
      <>
        <DataGrid
          dataSource={data?.data}
          allowColumnResizing={true}
          repaintChangesOnly={true}
          showBorders={true}
          height="79vh"
          onRowPrepared={onRowPrepared}
          onRowRemoved={onRowRemoved}
          onEditingStart={onEditingStart}
          onSaving={onSaving}
        >
          <Editing allowUpdating allowDeleting mode="popup">
            <Popup title="Edit" showTitle={true} height="auto" />
            <Form>
              <Item itemType="group" colCount={2} colSpan={2}>
                <Item dataField="billingCode" colSpan={2} isRequired={true} />
                <Item dataField="type" colSpan={2} isRequired={true} />
                <Item dataField="total" colSpan={2} isRequired={true} />
                <Item dataField="vendorName" colSpan={2} isRequired={true} />
                <Item dataField="date" colSpan={2} isRequired={true} />
                <Item dataField="description" colSpan={2} isRequired={true} />
              </Item>
            </Form>
          </Editing>

          <SearchPanel
            id="searchpanel"
            visible={true}
            maxWidth={275}
            searchVisibleColumnsOnly={true}
            placeholder="Search..."
          />
          <Toolbar>
            <Item
              location="before"
              locateInMenu="never"
              render={() => (
                <div className="misc-costs-title">
                  <h1 className="white">Misc Costs</h1>
                </div>
              )}
            />
            <Item location="after" locateInMenu="never">
              <FileUploader
                selectButtonText="Upload Excel"
                labelText=""
                accept=".xlsx, .xls"
                onValueChanged={handleFileUpload}
                uploadMode="useForm"
              />
            </Item>
            <Item name="searchPanel" />
          </Toolbar>

          <Column type="buttons">
            <DataButton name="edit" />
            <DataButton name="delete" />
          </Column>
          <Column dataField="id" width={100} alignment="left" />
          <Column dataField="billingCode">
            <Lookup
              dataSource={billingCodes}
              valueExpr="value"
              displayExpr="text"
            />
          </Column>
          <Column dataField="type">
            <Lookup
              dataSource={typeValues}
              valueExpr="value"
              displayExpr="text"
            />
          </Column>
          <Column dataField="total" caption="AMT" />
          <Column dataField="vendorName" />
          <Column dataField="date" dataType="date" format="MM/dd/yyyy" />
          <Column dataField="description" />
        </DataGrid>

        <Popup
          visible={popupVisible}
          onHiding={() => setPopupVisible(false)}
          title="Confirm Upload"
          closeOnOutsideClick
        >
          <DataGrid
            dataSource={input.map((row, index) => ({ index, ...row }))}
            showBorders={true}
            //selection={{ mode: 'multiple' }}
            //onSelectionChanged={(e) => setSelectedRows(e.selectedRowKeys)}
          >
            {/*<Selection mode="multiple" />*/}
            <Column dataField="id" caption="ID" />
            <Column dataField="billingCode" />
            <Column dataField="type" />
            <Column dataField="total" caption="AMT" />
            <Column dataField="vendorName" />
            <Column dataField="date" dataType="date" format="MM/dd/yyyy" />
            <Column dataField="description" />
          </DataGrid>
          <ToolbarItem
            widget="dxButton"
            toolbar="bottom"
            location="after"
            options={cancelOptions}
          />
          <ToolbarItem
            widget="dxButton"
            toolbar="bottom"
            location="after"
            options={submitOptions}
          />
        </Popup>
      </>
    );
  };

  if (!data) return <LoadingSpinnerModal show={true} />;

  return (
    <>
      <div className="px-lg-4 page-content">
        <ActionNotifications response={notifyResponse} />
        <div className="my-3 contentBox">
          {!error ? renderGrid() : <ErrorMessage />}
        </div>
      </div>
    </>
  );
}
